import { gql } from "@/graphql/typed-gql"

export const CREATE_SESSION = gql`
  mutation CreateSession(
    $studentId: ID!
    $subjectKey: String
    $customConfig: String
  ) {
    createSession(
      studentId: $studentId
      subjectKey: $subjectKey
      customConfig: $customConfig
    ) {
      session {
        id
        status
      }
      errors
    }
  }
`

export const PERSONALIZE_SESSION = gql`
  mutation PersonalizeSession($id: ID!, $profile: String) {
    personalizeSession(id: $id, profile: $profile) {
      session {
        id
        personalizationStatus
        personalizedProblemCount
        personalizationProfile
        exercises {
          id
          problemText
          personalized
        }
      }
      errors
    }
  }
`

export const GENERATE_EXERCISES = gql`
  mutation GenerateExercises(
    $sessionId: ID!
    $targetExerciseCount: Int
    $maxNewSkills: Int
    $includeDistantReview: Boolean
  ) {
    generateExercises(
      sessionId: $sessionId
      targetExerciseCount: $targetExerciseCount
      maxNewSkills: $maxNewSkills
      includeDistantReview: $includeDistantReview
    ) {
      session {
        id
        status
        exercises {
          id
          title
          instructions
          theory
          category
          skillKey
          categoryDisplay {
            label
            labelShort
            emoji
            color
            bgColor
            borderColor
            description
          }
          problemText
          expectedAnswer
          hint
          result
          skillKey
          skill {
            name
          }
        }
        sessionPlan {
          skillKey
          category
          exerciseCount
          skillName
          categoryDisplay {
            label
            labelShort
            emoji
            color
            bgColor
            borderColor
            description
          }
        }
        hasNewSkill
        newSkillKey
      }
      errors
      requestedCount
      generatedCount
      limits {
        code
        message
        detail
        option
        suggestedValue
        lost
      }
      missingSkills {
        skillKey
        skillName
        subjectKey
        grade
        level
        generateUrl
      }
    }
  }
`

export const REGENERATE_EXERCISE = gql`
  mutation RegenerateExercise($sessionId: ID!, $exerciseId: ID!) {
    regenerateExercise(sessionId: $sessionId, exerciseId: $exerciseId) {
      exercise {
        id
        title
        instructions
        theory
        category
        skillKey
        problemText
        expectedAnswer
        hint
        result
        skillKey
        skill {
          name
        }
      }
      errors
    }
  }
`

export const GRADE_SESSION = gql`
  mutation GradeSession($sessionId: ID!, $grades: JSON!) {
    gradeSession(sessionId: $sessionId, grades: $grades) {
      success
    }
  }
`

/**
 * Everything any other screen reads off a session, so finishing one cannot leave
 * a stale copy behind. The learner's profile, the home page and the session list
 * all read these fields from the normalized cache — and `completeSession` used to
 * answer with the points alone, so those screens went on showing a finished
 * session as unfinished, its questions as PENDING, until the page was reloaded.
 */
const SESSION_STATE_FIELDS = `
  id
  status
  createdAt
  updatedAt
  completedAt
  activeSeconds
  pointsEarned
  exerciseCount
  correctCount
  incorrectCount
  skippedCount
  totalCount
  exercises {
    id
    result
    userAnswer
    answerRevealed
    manuallyGraded
    neededHelp
    answeredLate
  }
`

export const COMPLETE_SESSION = gql`
  mutation CompleteSession($sessionId: ID!) {
    completeSession(sessionId: $sessionId) {
      success
      session {
        ${SESSION_STATE_FIELDS}
      }
      gamification {
        pointsEarned
        newTotalPoints
        level {
          level
          name
          icon
        }
        events {
          type
          points
          message
          achievementName
          achievementIcon
          levelName
          levelIcon
          skillName
        }
      }
    }
  }
`

export const SUBMIT_STUDENT_ANSWERS = gql`
  mutation SubmitStudentAnswers($sessionId: ID!, $answers: JSON!) {
    submitStudentAnswers(sessionId: $sessionId, answers: $answers) {
      success
      errors
    }
  }
`

export const CHECK_STUDENT_ANSWER = gql`
  mutation CheckStudentAnswer(
    $sessionId: ID!
    $exerciseId: ID!
    $answer: String
    $result: String
    $answerRevealed: Boolean
    $manuallyGraded: Boolean
    $elapsedSeconds: Int
  ) {
    checkStudentAnswer(
      sessionId: $sessionId
      exerciseId: $exerciseId
      answer: $answer
      result: $result
      answerRevealed: $answerRevealed
      manuallyGraded: $manuallyGraded
      elapsedSeconds: $elapsedSeconds
    ) {
      success
      # The stored question, so the cache cannot go on serving the PENDING copy
      # the solve page read when it opened.
      exercise {
        id
        result
        userAnswer
        answerRevealed
        manuallyGraded
        neededHelp
        answerSeconds
        answeredLate
      }
      result
      answeredLate
      errors
    }
  }
`

// Shared with GET_SESSION, so a conversation restored on page load is the same
// shape as one a mutation hands back. GET_SESSION used to keep its own copy, which
// lacked `id`: every restored chat had no id, `send` read that as "still being
// created", and a reply typed after any reload spun forever without a request.
export const DISCUSSION_FIELDS = `
  id
  status
  branch
  stage
  retryCorrect
  solvedInChat
  retryAnswer
  adjudication
  messages {
    id
    role
    content
    resolvedMarker
    suggestions
    reportedAt
  }
`

export const REQUEST_PROBLEM_HELP = gql`
  mutation RequestProblemHelp($sessionId: ID!, $exerciseId: ID!) {
    requestProblemHelp(sessionId: $sessionId, exerciseId: $exerciseId) {
      discussion {
        ${DISCUSSION_FIELDS}
      }
      errors
    }
  }
`

/**
 * Opens the tutoring conversation while `ADJUDICATE_ANSWER` is still running,
 * so a child who types straight away has somewhere to send it. No AI call, no
 * credit — every judgement still comes from adjudication.
 */
export const OPEN_ANSWER_DISCUSSION = gql`
  mutation OpenAnswerDiscussion($sessionId: ID!, $exerciseId: ID!, $answer: String!) {
    openAnswerDiscussion(sessionId: $sessionId, exerciseId: $exerciseId, answer: $answer) {
      discussion {
        ${DISCUSSION_FIELDS}
      }
      errors
    }
  }
`

export const ADJUDICATE_ANSWER = gql`
  mutation AdjudicateAnswer($sessionId: ID!, $exerciseId: ID!, $answer: String!) {
    adjudicateAnswer(sessionId: $sessionId, exerciseId: $exerciseId, answer: $answer) {
      result
      branch
      exerciseRewritten
      explanation
      degraded
      discussion {
        ${DISCUSSION_FIELDS}
      }
      errors
    }
  }
`

export const SEND_DISCUSSION_MESSAGE = gql`
  mutation SendDiscussionMessage($discussionId: ID!, $message: String!) {
    sendDiscussionMessage(discussionId: $discussionId, message: $message) {
      discussion {
        ${DISCUSSION_FIELDS}
      }
      problemResult
      errors
    }
  }
`

export const RESOLVE_DISCUSSION = gql`
  mutation ResolveDiscussion($discussionId: ID!, $skipped: Boolean) {
    resolveDiscussion(discussionId: $discussionId, skipped: $skipped) {
      discussion {
        ${DISCUSSION_FIELDS}
      }
      errors
    }
  }
`

export const DELETE_SESSION = gql`
  mutation DeleteSession($id: ID!) {
    deleteSession(id: $id) {
      success
    }
  }
`

export const RECORD_SESSION_ACTIVITY = gql`
  mutation RecordSessionActivity(
    $sessionId: ID!
    $seconds: Int!
    $exerciseId: ID
    $problemSeconds: Int
  ) {
    recordSessionActivity(
      sessionId: $sessionId
      seconds: $seconds
      exerciseId: $exerciseId
      problemSeconds: $problemSeconds
    ) {
      session {
        id
        activeSeconds
      }
      errors
    }
  }
`

export const RECORD_THEORY_VIEW = gql`
  mutation RecordTheoryView(
    $sessionId: ID!
    $exerciseId: ID!
    $seconds: Int!
    $autoOpened: Boolean
    $scrolledToEnd: Boolean
    $acknowledged: Boolean
  ) {
    recordTheoryView(
      sessionId: $sessionId
      exerciseId: $exerciseId
      seconds: $seconds
      autoOpened: $autoOpened
      scrolledToEnd: $scrolledToEnd
      acknowledged: $acknowledged
    ) {
      success
    }
  }
`

/**
 * "I think I was right." Records the learner's dispute and puts a copy in the
 * grown-up review queue. Nothing is regraded — see Mutations::RequestAnswerRecheck.
 */
export const REQUEST_ANSWER_RECHECK = gql`
  mutation RequestAnswerRecheck($sessionId: ID!, $exerciseId: ID!) {
    requestAnswerRecheck(sessionId: $sessionId, exerciseId: $exerciseId) {
      success
      recheckRequestedAt
      errors
    }
  }
`

/**
 * "I am not doing this one." A skip counts nowhere — not in the session total,
 * not in the skill or concept percentages, not in the credits — which is what
 * makes it safe to offer a learner. See Mutations::SkipExercise.
 */
export const SKIP_EXERCISE = gql`
  mutation SkipExercise($sessionId: ID!, $exerciseId: ID!) {
    skipExercise(sessionId: $sessionId, exerciseId: $exerciseId) {
      success
      exercise {
        id
        result
        userAnswer
      }
      result
      parked
      errors
    }
  }
`;

/**
 * "Something is wrong with this question." A report about the content itself,
 * not about the grading — see Mutations::ReportExerciseProblem for why the two
 * are kept apart. Lands in the same queue at /admin/flagged.
 */
/**
 * „Pranešti" on one message the AI tutor wrote. Answers with the message, so
 * the chat says „Pranešta" without a refetch.
 */
export const REPORT_AI_MESSAGE = gql`
  mutation ReportAiMessage($messageId: ID!, $reason: String!, $comment: String) {
    reportAiMessage(messageId: $messageId, reason: $reason, comment: $comment) {
      success
      reportedAt
      message {
        id
        reportedAt
      }
      errors
    }
  }
`;

export const REPORT_EXERCISE_PROBLEM = gql`
  mutation ReportExerciseProblem(
    $sessionId: ID!
    $exerciseId: ID!
    $reason: String!
    $comment: String
  ) {
    reportExerciseProblem(
      sessionId: $sessionId
      exerciseId: $exerciseId
      reason: $reason
      comment: $comment
    ) {
      success
      reportedAt
      # SKIPPED now, if the session is still in progress — so every screen
      # reading the cached question stops showing the graded copy.
      exercise {
        id
        result
        reportedAt
      }
      errors
    }
  }
`
